/**
 * AI 주간 보고서 — 기업 현황
 * ---------------------------------------------------------------------------
 * 대시보드는 "지금 어떤가" 를 보여 준다. 이 보고서는 **고른 기간에 무엇이
 * 달라졌는지**를 글로 정리해 준다. 매주 윗선에 올리는 그 한 장이다.
 *
 * ## 누르기 전에 금액을 보여 준다
 * 1건마다 요금이 청구서에 붙는다. 만들고 나서 "얼마 나왔습니다" 를 보여
 * 주면 늦다. 단추에 금액을 적어 둔다. 실패하면 청구하지 않는다 —
 * 플랫폼이 모델 호출에 성공한 뒤에만 건수를 적는다.
 *
 * ## 기간은 1개월까지
 * 진짜로 막는 쪽은 서버다. 여기서 또 막는 이유는 거절당하기 전에 알려 주기
 * 위해서다 — 고를 수 없는 날짜가 회색인 편이 빨간 글씨보다 낫다.
 *
 * ## 개인정보는 모델에 가지 않는다
 * 서버가 모으는 자료는 숫자와 분류뿐이다. 누가 무엇을 했는지는 [로그 조회]
 * 화면에서 본다.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Checkbox,
  DatePicker,
  Modal,
  Space,
  Spin,

  Typography,
} from 'antd';
import { RobotOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import { apiClient, type NormalizedError } from '../../lib/api-client';
import { errorText } from '../../lib/queries';
import { hasPermission, useAuth } from '../../lib/auth-store';

const { Paragraph, Text } = Typography;

interface AiConfig {
  enabled: boolean;
  providerName: string;
  pricePerReport: number | null;
  maxInputChars: number;
  disabledReason: string | null;
  /** 이번 달 무료 건수 — 기업 단위다(사람 수와 무관) */
  freePerMonth: number;
  usedThisMonth: number;
  freeRemaining: number;
  periodStart: string;
  periodEnd: string;
  /** 동의 문구는 서버가 준다 — 세 제품이 같은 문장을 띄워야 한다 */
  consentText: string;
}

/**
 * 이번 달에 몇 건을 만들었고, 무료는 월 몇 건인가.
 *
 * ## 한 줄로 끝낸다
 * 처음에는 「무료 N건 중 M건 사용 · 남은 무료 K건 · 초과 시 1건 ○○○원」에
 * 설명 문단까지 붙여 두었다. 창을 열자마자 읽을 것이 너무 많아 정작
 * 고르려던 기간이 아래로 밀렸다. 알아야 할 것은 두 가지뿐이다 —
 * <b>이번 달에 몇 건 썼나</b>, <b>무료는 몇 건까지인가.</b> 돈이 붙는
 * 시점은 아래 안내에서 한 번 더 말한다.
 *
 * 세 제품(Hub·ECO·CRM)이 같은 문장을 쓴다. 한 곳만 고치면 갈라진다.
 */
/** `2026-10-01` → `2026년 10월`. 과금 기간의 첫날을 사람이 읽는 달 이름으로. */
function monthLabel(periodStart: string | null | undefined) {
  return periodStart
    ? `${periodStart.slice(0, 4)}년 ${Number(periodStart.slice(5, 7))}월`
    : '이번 달';
}

function QuotaLine({ config }: { config: AiConfig }) {
  const month = monthLabel(config.periodStart);

  return (
    <Alert
      type={config.freeRemaining > 0 ? 'success' : 'warning'}
      showIcon
      style={{ padding: '6px 12px' }}
      message={
        <Text>
          {month}: 보고서 생성 <b>{config.usedThisMonth}건</b>
          <Text type="secondary">(무료 제공 {config.freePerMonth}건/월)</Text>
        </Text>
      }
    />
  );
}

/**
 * 화면 설명 안에 들어가는 <b>살아 있는</b> 사용량 한 줄.
 *
 * ## 왜 숫자를 적어 두지 않나
 * 설명에 「무료 제공 5건/월」처럼 숫자를 박아 두면, 요금제가 바뀌는 순간
 * <b>설명만 거짓말이 된다.</b> 실제로 그랬다 — 무료 수량을 내린 뒤에도 설명은
 * 예전 숫자를 말하고 있었다. 돈에 관한 숫자라 더 나쁘다.
 *
 * 그래서 {@link QuotaLine} 과 <b>같은 값·같은 서식</b>을 요금제에서 읽어 와
 * 그대로 보여 준다. 질의 키가 창의 것과 같아 이미 읽어 둔 값이 있으면 그대로 쓴다.
 */
export function AiQuotaExample() {
  const { data, isLoading } = useQuery<AiConfig>({
    queryKey: ['ai-report', 'config'],
    queryFn: async () => (await apiClient.get<AiConfig>('/ai-report/config')).data,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  if (isLoading) return <b>「…」</b>;
  if (!data?.enabled) return <b>「이번 달: 보고서 생성 ○건(무료 제공 ○건/월)」</b>;

  return (
    <b>
      「{monthLabel(data.periodStart)}: 보고서 생성 {data.usedThisMonth}건 (무료 제공{' '}
      {data.freePerMonth}건/월)」
    </b>
  );
}

interface AiResult {
  text: string;
  providerName: string;
  pricePerReport: number;
}

/**
 * 보고서 1건이 곧 청구 1건이다. 그래서 자사 현황을 볼 수 있는 사람이라고
 * 모두에게 보여 주지 않는다 — 그 화면은 이제 일반 구성원에게도 열리는데,
 * `/ai-report` 는 platform.tenant:read 를 요구하므로 눌러도 403 이 된다.
 * 눌리지 않을 단추는 아예 그리지 않는다.
 */
export function AiWeeklyReportButton() {
  const [open, setOpen] = useState(false);
  const { profile } = useAuth();

  if (!hasPermission(profile, 'platform.tenant:read')) return null;

  return (
    <>
      <Button icon={<RobotOutlined />} onClick={() => setOpen(true)}>
        AI 주간 보고서
      </Button>
      {/* 열 때 비로소 설정을 불러온다 — 쓰지 않는 사람에게 매번 호출할 이유가 없다. */}
      {open && <AiWeeklyReportModal onClose={() => setOpen(false)} />}
    </>
  );
}

/*
 * 지난 보고서 보관함은 **화면으로 옮겼다** (AiReportArchivePage).
 *
 * 전에는 이 팝업 안 탭에 있었는데, 보고서를 만든 적 없는 사람은 그런 것이
 * 있는 줄도 몰랐다. 쌓인 것을 보는 자리는 메뉴에 있어야 한다 — 왼쪽
 * 「기록 → AI 보고서」 다. 여기는 만드는 자리만 맡는다.
 *
 * 두 벌로 두지 않는다. 목록·상세·삭제가 두 곳에 있으면 한쪽만 고쳐진다.
 */

function AiWeeklyReportModal({ onClose }: { onClose: () => void }) {
  const { message } = App.useApp();
  const client = useQueryClient();

  // 기본값은 지난 7일. "주간" 보고서라서다.
  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().subtract(6, 'day'), dayjs()]);
  const [result, setResult] = useState<AiResult | null>(null);

  /*
   * 동의는 **만들 때마다** 새로 받는다. 한 번 체크해 두고 계속 쓰게 하면
   * 「그때 동의했다」 가 아니라 「예전에 한 번 체크했다」 가 된다.
   */
  const [consent, setConsent] = useState(false);

  const config = useQuery<AiConfig, NormalizedError>({
    queryKey: ['ai-report', 'config'],
    queryFn: async () => (await apiClient.get<AiConfig>('/ai-report/config')).data,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const generate = useMutation<AiResult, NormalizedError>({
    mutationFn: async () =>
      (
        await apiClient.post<AiResult>('/ai-report', null, {
          params: {
            periodStart: range[0].format('YYYY-MM-DD'),
            periodEnd: range[1].format('YYYY-MM-DD'),
            consent: true,
          },
        })
      ).data,
    onSuccess: (r) => {
      setResult(r);
      // 방금 만든 것이 보관함에도 보여야 한다.
      void client.invalidateQueries({ queryKey: ['ai-report', 'list'] });
      /*
       * 사용 건수가 하나 늘었다. 다시 읽지 않으면 「무료 5건 중 2건 사용」이
       * 그대로 남아, 다음에 누를 때 돈이 나가는지 아닌지를 틀리게 보여 준다.
       */
      void client.invalidateQueries({ queryKey: ['ai-report', 'config'] });
      setConsent(false);
    },
    onError: (err) => message.error(errorText(err)),
  });

  const days = range[1].diff(range[0], 'day') + 1;


  return (
    <Modal
      open
      width={820}
      title="AI 주간 보고서"
      onCancel={onClose}
      maskClosable={!generate.isPending}
      footer={
        result
          ? [
              <Button key="again" onClick={() => setResult(null)}>
                다시 만들기
              </Button>,
              <Button
                key="copy"
                type="primary"
                onClick={() => {
                  navigator.clipboard
                    .writeText(result.text)
                    .then(() => message.success('복사했습니다'))
                    .catch(() => message.error('복사하지 못했습니다. 글을 직접 선택해 주세요.'));
                }}
              >
                복사
              </Button>,
            ]
          : [
              <Button key="cancel" onClick={onClose} disabled={generate.isPending}>
                닫기
              </Button>,
              <Button
                key="go"
                type="primary"
                icon={<RobotOutlined />}
                loading={generate.isPending}
                /* 동의 없이는 누를 수 없다. 서버도 같은 이유로 거절한다. */
                disabled={!config.data?.enabled || !consent}
                onClick={() => generate.mutate()}
              >
                보고서 만들기
              </Button>,
            ]
      }
    >
      {config.isLoading ? (
        <Spin />
      ) : result ? (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {/*
            결과 보고서에는 엔진도 금액도 적지 않는다. 받아서 올리는 글에
            「Claude · 350원 청구」가 붙으면 보고서가 아니라 영수증이 된다.
          */}
          <Paragraph
            style={{
              whiteSpace: 'pre-wrap',
              maxHeight: '55vh',
              overflowY: 'auto',
              background: '#fafafa',
              border: '1px solid #f0f0f0',
              borderRadius: 8,
              padding: 16,
              margin: 0,
            }}
          >
            {result.text}
          </Paragraph>
        </Space>
      ) : (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {config.isError && <Alert type="error" showIcon message={errorText(config.error)} />}

          {config.data && !config.data.enabled && (
            <Alert
              type="warning"
              showIcon
              message="지금은 쓸 수 없습니다"
              description={config.data.disabledReason ?? 'AI 보고서를 쓸 수 없는 상태입니다.'}
            />
          )}

          {config.data?.enabled && <QuotaLine config={config.data} />}

          <div>
            <Text strong>분석 기간</Text>
            <div style={{ marginTop: 8 }}>
              <DatePicker.RangePicker
                value={range}
                allowClear={false}
                onChange={(v) => {
                  if (v?.[0] && v[1]) setRange([v[0], v[1]]);
                }}
                /*
                  오늘 이후는 고를 수 없고, 한쪽을 집은 뒤에는 그 날로부터
                  1개월 밖이 닫힌다. 서버와 같은 셈이다(달 단위) — 30일로
                  어림잡으면 2월에 어긋난다.
                */
                disabledDate={(d, info) => {
                  if (d.isAfter(dayjs(), 'day')) return true;
                  if (!info.from) return false;
                  return d.isAfter(info.from)
                    ? d.isAfter(info.from.add(1, 'month'), 'day')
                    : d.isBefore(info.from.subtract(1, 'month'), 'day');
                }}
              />
              <Text type="secondary" style={{ marginLeft: 12 }}>
                {days}일
              </Text>
            </div>
            <Text type="secondary" style={{ fontSize: 13 }}>
              최대 1개월까지 분석 가능
            </Text>
          </div>

          {config.data?.enabled && (
            <Alert
              type="info"
              showIcon
              style={{ padding: '6px 12px' }}
              message={
                <>
                  이 기간의 구성원 변동·접속·로그인 실패·사용량·청구·활동 기록을 모아 AI 가
                  정리합니다.{' '}
                  <b>
                    무료 수량 초과 시 건당 비용이 발생합니다.
                    {config.data.pricePerReport !== null &&
                      `(${config.data.pricePerReport.toLocaleString()}원/건)`}
                  </b>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    이름·이메일 같은 개인정보는 보내지 않습니다.
                  </Text>
                </>
              }
            />
          )}

          {config.data?.enabled && (
            <Checkbox
              checked={consent}
              disabled={generate.isPending}
              onChange={(e) => setConsent(e.target.checked)}
            >
              {config.data.consentText}
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  선택된 모델: <b>{config.data.providerName}</b> · 동의 여부는 보고서에 함께
                  기록됩니다.
                </Text>
              </div>
            </Checkbox>
          )}

          {generate.isPending && (
            <Space>
              <Spin />
              <Text type="secondary">
                보고서를 만들고 있습니다. 보통 20~60초 걸립니다 — 이 창을 닫지 말아 주세요.
              </Text>
            </Space>
          )}
        </Space>
      )}
    </Modal>
  );
}
